/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { LuChevronDown } from 'react-icons/lu';

import {
   CountBadge,
   EmptyOption,
   Footer,
   Option,
   OptionList,
   Popover,
   Root,
   SearchInput,
   SearchRow,
   Trigger,
   TriggerLabel,
} from './styles';
import { SmallButton } from '../../styles/ui/Button';

// ---------------------------------------------------------------------------
// MultiSelectDropdown — seleção múltipla em popover com checkbox.
//
// Nasceu do filtro de origem do dashboard de vendas a prazo: 21 chips soltos
// numa linha viram uma parede de texto que empurra o resto da tela. O dropdown
// devolve o espaço e mantém a informação no gatilho (rótulo + contador), que é
// o que impede um filtro fechado de virar caixa-preta.
//
// Fica em `components/` (não na pasta da tela) porque não tem nada de
// financeiro: qualquer listagem com filtro multi-valor usa igual.
//
// Comportamento:
//  · fecha com Esc, clique fora e blur para fora do popover;
//  · busca aparece sozinha a partir de `searchThreshold` opções;
//  · nada selecionado = "todas" (a semântica de filtro vazio do backend);
//  · `onChange` recebe o array novo inteiro — o componente não guarda estado
//    de seleção, só de abertura. Quem manda na seleção é a URL da tela.
// ---------------------------------------------------------------------------

export default function MultiSelectDropdown({
   label,
   options,
   selected,
   onChange,
   emptyLabel = 'Todas',
   searchPlaceholder = 'Buscar...',
   searchThreshold = 8,
   disabled = false,
}) {
   const [open, setOpen] = useState(false);
   const [query, setQuery] = useState('');
   const rootRef = useRef(null);
   const searchRef = useRef(null);

   const selectedSet = useMemo(() => new Set(selected), [selected]);

   const filtered = useMemo(() => {
      const term = query.trim().toLowerCase();
      if (!term) return options;
      return options.filter((option) =>
         String(option.label).toLowerCase().includes(term),
      );
   }, [options, query]);

   const showSearch = options.length >= searchThreshold;

   // Esc fecha e devolve o foco ao gatilho; clique fora fecha. Sem isso o
   // popover fica preso aberto para quem navega por teclado.
   useEffect(() => {
      if (!open) return undefined;

      const handlePointerDown = (event) => {
         if (!rootRef.current?.contains(event.target)) setOpen(false);
      };
      const handleKeyDown = (event) => {
         if (event.key === 'Escape') {
            setOpen(false);
            rootRef.current?.querySelector('button')?.focus();
         }
      };

      document.addEventListener('mousedown', handlePointerDown);
      document.addEventListener('keydown', handleKeyDown);
      return () => {
         document.removeEventListener('mousedown', handlePointerDown);
         document.removeEventListener('keydown', handleKeyDown);
      };
   }, [open]);

   useEffect(() => {
      if (open && showSearch) searchRef.current?.focus();
      if (!open) setQuery('');
   }, [open, showSearch]);

   const toggleValue = (value) => {
      onChange(
         selectedSet.has(value)
            ? selected.filter((entry) => entry !== value)
            : [...selected, value],
      );
   };

   // Rótulo do gatilho: com 1 selecionado mostra qual (mais útil que "1"); com
   // vários, o contador basta.
   const triggerText = () => {
      if (!selected.length) return emptyLabel;
      if (selected.length === 1) {
         const match = options.find((option) => option.value === selected[0]);
         return match?.label ?? emptyLabel;
      }
      return label;
   };

   return (
      <Root ref={rootRef}>
         <Trigger
            type="button"
            disabled={disabled}
            $active={open || selected.length > 0}
            aria-expanded={open}
            aria-haspopup="true"
            onClick={() => setOpen((value) => !value)}
         >
            <TriggerLabel>{triggerText()}</TriggerLabel>
            {selected.length > 1 ? (
               <CountBadge>{selected.length}</CountBadge>
            ) : null}
            <LuChevronDown size={15} strokeWidth={1.8} aria-hidden="true" />
         </Trigger>

         {open ? (
            <Popover role="dialog" aria-label={label}>
               {showSearch ? (
                  <SearchRow>
                     <SearchInput
                        ref={searchRef}
                        type="text"
                        value={query}
                        placeholder={searchPlaceholder}
                        onChange={(event) => setQuery(event.target.value)}
                     />
                  </SearchRow>
               ) : null}

               <OptionList>
                  {filtered.length ? (
                     filtered.map((option) => (
                        <Option key={option.value}>
                           <input
                              type="checkbox"
                              checked={selectedSet.has(option.value)}
                              onChange={() => toggleValue(option.value)}
                           />
                           <span>{option.label}</span>
                        </Option>
                     ))
                  ) : (
                     <EmptyOption>Nenhuma opção encontrada.</EmptyOption>
                  )}
               </OptionList>

               <Footer>
                  <span>
                     {selected.length
                        ? `${selected.length} de ${options.length}`
                        : `${emptyLabel} (${options.length})`}
                  </span>
                  {selected.length ? (
                     <SmallButton type="button" onClick={() => onChange([])}>
                        Limpar
                     </SmallButton>
                  ) : null}
               </Footer>
            </Popover>
         ) : null}
      </Root>
   );
}

MultiSelectDropdown.propTypes = {
   label: PropTypes.string.isRequired,
   options: PropTypes.arrayOf(
      PropTypes.shape({
         value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
            .isRequired,
         label: PropTypes.node.isRequired,
      }),
   ).isRequired,
   selected: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
   ).isRequired,
   onChange: PropTypes.func.isRequired,
   emptyLabel: PropTypes.string,
   searchPlaceholder: PropTypes.string,
   searchThreshold: PropTypes.number,
   disabled: PropTypes.bool,
};
